import type { BulkApplyResult, BulkColumn, BulkReferencePort, BulkValidatedRow, BulkWorkflow } from '../../bulk/public';
import type {
  ReferenceDataCreateInput,
  ReferenceDataItem,
  ReferenceJobFamily,
  ReferenceJobLevelCreateInput,
  ReferenceJobProfileCreateInput,
  ReferenceJobType,
  ReferenceJobTypeCreateInput,
  ReferenceLocationCreateInput,
  ReferenceLocationType,
  ReferenceWorkSpaceCreateInput,
} from './types';
import { locationTypeLabel, validDate, validDescriptor, validReason, workerTypeLabel } from './referenceData';

const referenceTemplateHref = '/templates/bulk-upload-templates.xlsx';
const maxRows = 200;

export async function applyReferenceBulkRows<T extends { id: string }>(
  rows: readonly BulkValidatedRow[],
  batchKey: string,
  create: (row: Record<string, string>, idempotencyKey: string) => Promise<T>,
): Promise<{ result: BulkApplyResult; created: T[] }> {
  const created: T[] = [];
  const results: BulkApplyResult['rows'] = [];
  for (const row of rows) {
    try {
      const item = await create(row.values, `${batchKey}:row:${row.rowNumber}`);
      created.push(item);
      results.push({ rowNumber: row.rowNumber, status: 'SUCCEEDED', resourceId: item.id });
    } catch (cause) {
      results.push({
        rowNumber: row.rowNumber,
        status: 'FAILED',
        message: cause instanceof Error ? cause.message : '서버에서 이 행을 등록하지 못했습니다.',
      });
    }
  }
  return { result: { rows: results }, created };
}

const descriptorValidation = (value: string) =>
  validDescriptor(value) ? undefined : '이름은 1자 이상 120자 이하여야 합니다.';
const dateValidation = (value: string) =>
  validDate(value) ? undefined : '적용일은 실제 날짜를 YYYY-MM-DD 형식으로 입력해 주세요.';
const reasonValidation = (value: string) =>
  validReason(value) ? undefined : '변경 사유는 1자 이상 500자 이하여야 합니다.';

const commonColumns = (): BulkColumn[] => [
  { key: 'descriptor', label: '이름', required: true, example: '플랫폼 엔지니어', validate: descriptorValidation },
  { key: 'effectiveDate', label: '적용일', required: true, example: '2026-09-15', validate: dateValidation },
  { key: 'reason', label: '변경 사유', required: true, example: '기준정보 초기 등록', validate: reasonValidation },
];

const commonPayload = (row: Record<string, string>): ReferenceDataCreateInput => ({
  descriptor: row.descriptor.trim(),
  effectiveDate: row.effectiveDate,
  reason: row.reason.trim(),
});

function findActiveReference<T extends ReferenceDataItem>(items: readonly T[], input: string) {
  const active = items.filter((item) => item.status === 'ACTIVE');
  const exactId = active.find((item) => item.id === input.trim());
  if (exactId) return exactId;
  const normalized = input.trim().toLocaleLowerCase();
  const matches = active.filter((item) => item.code.toLocaleLowerCase() === normalized);
  return matches.length === 1 ? matches[0] : undefined;
}

/** 기준정보 화면에서 이미 권한을 확인하고 조회한 부모 목록 안에서만 연결합니다. */
export function createReferenceBulkPort(field: 'locationCode' | 'jobFamilyCode', items: readonly ReferenceDataItem[]): BulkReferencePort {
  return {
    async search(requestedField, query) {
      if (requestedField !== field) throw new Error('이 화면에서 선택할 수 없는 기준정보입니다.');
      const search = query.trim().toLocaleLowerCase();
      return items.filter((item) => item.status === 'ACTIVE')
        .filter((item) => !search || [item.id, item.descriptor, item.code].some((value) => value.toLocaleLowerCase().includes(search)))
        .map((item) => ({
          id: item.id,
          label: item.descriptor,
          description: `적용일 ${item.effectiveFrom} · ${item.code}`,
          aliases: [item.code],
        }));
    },
  };
}

export const referenceBulkWorkflows = {
  locations(): BulkWorkflow {
    const types: ReferenceLocationType[] = ['OFFICE', 'REMOTE', 'CLIENT_SITE', 'OTHER'];
    return {
      operationType: 'REFERENCE_LOCATION_CREATE',
      label: '근무지 대량 등록',
      description: '여러 근무지를 한 번에 등록합니다.',
      resourceLabel: '근무지',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: [
        { key: 'descriptor', label: '이름', required: true, example: '성수 오피스', validate: descriptorValidation },
        {
          key: 'locationType',
          label: '유형',
          required: true,
          example: 'OFFICE',
          validate: (value) => types.includes(value as ReferenceLocationType) ? undefined : '유형은 OFFICE, REMOTE, CLIENT_SITE, OTHER 중 하나여야 합니다.',
        },
        {
          key: 'countryCode',
          label: '국가 코드',
          required: true,
          example: 'KR',
          validate: (value) => /^[A-Za-z]{2}$/.test(value) ? undefined : '국가 코드는 영문 두 자리여야 합니다.',
        },
        {
          key: 'timeZone',
          label: '시간대',
          required: true,
          example: 'Asia/Seoul',
          validate: (value) => value.trim().length > 0 && value.trim().length <= 100 ? undefined : '시간대는 100자 이하로 입력해 주세요.',
        },
        ...commonColumns().slice(1),
      ],
      previewColumns: [
        { label: '근무지', value: (row) => row.descriptor },
        { label: '유형', value: (row) => locationTypeLabel(row.locationType as ReferenceLocationType) },
        { label: '국가·시간대', value: (row) => `${row.countryCode.toUpperCase()} · ${row.timeZone}` },
        { label: '적용일', value: (row) => row.effectiveDate },
      ],
    };
  },
  workSpaces(locations: readonly ReferenceDataItem[]): BulkWorkflow {
    return {
      operationType: 'REFERENCE_WORK_SPACE_CREATE',
      label: '업무 공간 대량 등록',
      description: '근무지 이름으로 여러 업무 공간을 등록합니다. 같은 이름은 검증 단계에서 검색해 선택합니다.',
      resourceLabel: '업무 공간',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: [
        { ...commonColumns()[0], example: '6층 회의실' },
        { key: 'locationCode', label: '근무지', required: true, example: '서울 본사' },
        ...commonColumns().slice(1),
      ],
      previewColumns: [
        { label: '업무 공간', value: (row) => row.descriptor },
        { label: '근무지', referenceKey: 'locationCode', value: (row) => findActiveReference(locations, row.locationCode)?.descriptor ?? row.locationCode },
        { label: '적용일', value: (row) => row.effectiveDate },
      ],
    };
  },
  jobTypes(): BulkWorkflow {
    const workerTypes: ReferenceJobType['workerType'][] = ['EMPLOYEE', 'CONTINGENT_WORKER'];
    return {
      operationType: 'REFERENCE_JOB_TYPE_CREATE',
      label: '고용·외부 인력 유형 대량 등록',
      description: '여러 고용 형태와 외부 인력 유형을 한 번에 등록합니다.',
      resourceLabel: '고용·외부 인력 유형',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: [
        ...commonColumns().slice(0, 1),
        {
          key: 'workerType',
          label: '적용 대상',
          required: true,
          example: 'EMPLOYEE',
          validate: (value) => workerTypes.includes(value as ReferenceJobType['workerType']) ? undefined : '적용 대상은 EMPLOYEE 또는 CONTINGENT_WORKER여야 합니다.',
        },
        ...commonColumns().slice(1),
      ],
      previewColumns: [
        { label: '유형', value: (row) => row.descriptor },
        { label: '적용 대상', value: (row) => workerTypeLabel(row.workerType as ReferenceJobType['workerType']) },
        { label: '적용일', value: (row) => row.effectiveDate },
      ],
    };
  },
  jobFamilies(): BulkWorkflow {
    return {
      operationType: 'REFERENCE_JOB_FAMILY_CREATE',
      label: '직군 대량 등록',
      description: '여러 직군을 한 번에 등록합니다.',
      resourceLabel: '직군',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: commonColumns(),
      previewColumns: [
        { label: '직군', value: (row) => row.descriptor },
        { label: '적용일', value: (row) => row.effectiveDate },
        { label: '변경 사유', value: (row) => row.reason },
      ],
    };
  },
  jobProfiles(families: readonly ReferenceJobFamily[]): BulkWorkflow {
    return {
      operationType: 'REFERENCE_JOB_PROFILE_CREATE',
      label: '직무 대량 등록',
      description: '직군 이름으로 여러 직무를 등록합니다. 같은 이름은 검증 단계에서 검색해 선택합니다.',
      resourceLabel: '직무',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: [
        ...commonColumns().slice(0, 1),
        { key: 'jobFamilyCode', label: '직군', required: true, example: '엔지니어링' },
        ...commonColumns().slice(1),
      ],
      previewColumns: [
        { label: '직무', value: (row) => row.descriptor },
        { label: '직군', referenceKey: 'jobFamilyCode', value: (row) => findActiveReference(families, row.jobFamilyCode)?.descriptor ?? row.jobFamilyCode },
        { label: '적용일', value: (row) => row.effectiveDate },
      ],
    };
  },
  jobLevels(): BulkWorkflow {
    return {
      operationType: 'REFERENCE_JOB_LEVEL_CREATE',
      label: '직무 레벨 대량 등록',
      description: '표시 순서를 포함해 여러 직무 레벨을 등록합니다.',
      resourceLabel: '직무 레벨',
      templateHref: referenceTemplateHref,
      maxRows,
      columns: [
        ...commonColumns().slice(0, 1),
        {
          key: 'sortOrder',
          label: '정렬 순서',
          required: true,
          example: '10',
          validate: (value) => {
            const order = Number(value);
            return Number.isInteger(order) && order >= 0 && order <= 100000 ? undefined : '정렬 순서는 0 이상 100000 이하의 정수여야 합니다.';
          },
        },
        ...commonColumns().slice(1),
      ],
      previewColumns: [
        { label: '직무 레벨', value: (row) => row.descriptor },
        { label: '정렬 순서', value: (row) => row.sortOrder },
        { label: '적용일', value: (row) => row.effectiveDate },
      ],
    };
  },
};

export const referenceBulkPayloads = {
  location(row: Record<string, string>): ReferenceLocationCreateInput {
    return {
      ...commonPayload(row),
      locationType: row.locationType as ReferenceLocationType,
      countryCode: row.countryCode.trim().toUpperCase(),
      timeZone: row.timeZone.trim(),
    };
  },
  workSpace(row: Record<string, string>, locations: readonly ReferenceDataItem[]): ReferenceWorkSpaceCreateInput {
    const location = findActiveReference(locations, row.locationCode);
    if (!location) throw new Error('사용 중인 근무지를 찾을 수 없습니다.');
    return { ...commonPayload(row), location: { id: location.id } };
  },
  jobType(row: Record<string, string>): ReferenceJobTypeCreateInput {
    return { ...commonPayload(row), workerType: row.workerType as ReferenceJobType['workerType'] };
  },
  jobFamily: commonPayload,
  jobProfile(row: Record<string, string>, families: readonly ReferenceJobFamily[]): ReferenceJobProfileCreateInput {
    const family = findActiveReference(families, row.jobFamilyCode);
    if (!family) throw new Error('사용 중인 직군을 찾을 수 없습니다.');
    return { ...commonPayload(row), jobFamily: { id: family.id } };
  },
  jobLevel(row: Record<string, string>): ReferenceJobLevelCreateInput {
    return { ...commonPayload(row), sortOrder: Number(row.sortOrder) };
  },
};
